import { Injectable, signal } from '@angular/core';
import { PWA_INSTALLED_STORAGE_KEY } from '../utils/browser-access-gate';
import { environment } from '../../../environments/environment';

/** Chromium-only event fired when the browser deems the PWA installable. */
interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

/** Subset of the `navigator.getInstalledRelatedApps()` result we read. */
interface RelatedApplication {
  platform?: string;
  id?: string;
  url?: string;
}

/**
 * Deep link that focuses/opens this environment's installed PWA instead of the
 * browser tab. The scheme is env-specific (`environment.pwaLaunchProtocol`) so
 * dev/staging/prod each route to their own installed instance — a shared scheme
 * would make every "Launch app" open whichever env installed last.
 */
export function pwaLaunchUrl(): string {
  return `${environment.pwaLaunchProtocol}://open`;
}

/**
 * How long to wait for an install signal (`beforeinstallprompt` or a
 * `getInstalledRelatedApps` result) before declaring detection "settled" so the
 * UI can fall back to manual install steps. Covers browsers that fire nothing
 * (iOS Safari, Firefox) without flashing the wrong action on Chromium, where
 * `beforeinstallprompt` normally arrives within a few hundred ms of load.
 */
export const PWA_INSTALL_SETTLE_MS = 1200;

/**
 * Owns the browser's `beforeinstallprompt` handshake and the "open the
 * installed app" deep link, shared by every surface that offers install/open
 * (the dismissible `PwaInstallPromptComponent` and the `isBrowserBlocked`
 * hard-block screen). Root-provided singleton so the deferred prompt is
 * captured once, no matter how many surfaces read it.
 *
 * Mirrors the codebase's service shape (`SingleInstanceService`,
 * `AppUpdateService`): a guarded `init()` called once from `App.ngOnInit()`
 * rather than doing work in the constructor, so bootstrap has one obvious place
 * to start listening — and the listener is registered as early as possible so
 * an early `beforeinstallprompt` is not missed.
 */
@Injectable({ providedIn: 'root' })
export class PwaInstallService {
  /** True once the browser has offered a programmatic install (Chromium desktop/Android). */
  readonly canInstall = signal(false);
  /** True once an installed instance of this PWA has been detected. */
  readonly installed = signal(false);
  /**
   * True once install detection has finished — either a signal arrived or the
   * settle grace elapsed. The UI waits for this before showing the manual
   * fallback, so it never flashes the wrong action on Chromium.
   */
  readonly settled = signal(false);

  private deferredPrompt: BeforeInstallPromptEvent | null = null;
  private initialized = false;

  private readonly onBeforeInstall = (event: Event): void => {
    // Suppress Chrome's default mini-infobar so the app can offer install on its own terms.
    event.preventDefault();
    this.deferredPrompt = event as BeforeInstallPromptEvent;
    this.canInstall.set(true);
    // `beforeinstallprompt` only fires when the app is NOT installed, so it also
    // proves any earlier "installed" state is stale — e.g. the user uninstalled
    // but the persisted flag survived (uninstall doesn't clear site storage).
    // Clear it so we show "Install", not a dead "Launch app" deep link.
    this.installed.set(false);
    this.clearPersistedInstalled();
  };

  private readonly onAppInstalled = (): void => {
    // The user just installed from this tab — flip to the "open the app" affordance
    // and persist it so later browser-tab visits know the app is installed.
    this.deferredPrompt = null;
    this.canInstall.set(false);
    this.markInstalled();
  };

  /**
   * Starts install detection once; safe to call repeatedly. Registers the
   * `beforeinstallprompt`/`appinstalled` listeners, probes
   * `getInstalledRelatedApps()` (Chromium) for a pre-existing install, and arms
   * the settle grace so the UI can fall back to manual steps on browsers that
   * signal nothing.
   */
  init(): void {
    if (this.initialized) return;
    this.initialized = true;

    // Optimistically trust the persisted flag: an installed PWA shares
    // localStorage with browser tabs on this origin, so a prior standalone run
    // marks the app installed for later tabs. If it is stale (the user
    // uninstalled — which doesn't clear site storage), `beforeinstallprompt`
    // will fire and clear it (see onBeforeInstall).
    if (this.readPersistedInstalled()) this.installed.set(true);

    window.addEventListener('beforeinstallprompt', this.onBeforeInstall);
    window.addEventListener('appinstalled', this.onAppInstalled);

    const nav = navigator as Navigator & {
      getInstalledRelatedApps?: () => Promise<RelatedApplication[]>;
    };
    if (typeof nav.getInstalledRelatedApps === 'function') {
      this.detectExistingInstall(nav.getInstalledRelatedApps.bind(nav));
    }
    setTimeout(() => this.settled.set(true), PWA_INSTALL_SETTLE_MS);
  }

  private readPersistedInstalled(): boolean {
    try {
      return localStorage.getItem(PWA_INSTALLED_STORAGE_KEY) === '1';
    } catch {
      return false;
    }
  }

  private markInstalled(): void {
    this.installed.set(true);
    try {
      localStorage.setItem(PWA_INSTALLED_STORAGE_KEY, '1');
    } catch {
      // Private mode / storage disabled — the in-memory signal still drives this session.
    }
  }

  private clearPersistedInstalled(): void {
    try {
      localStorage.removeItem(PWA_INSTALLED_STORAGE_KEY);
    } catch {
      // Storage unavailable — nothing to clear.
    }
  }

  /**
   * Forget the installed state — for when the user tells us the app was removed.
   * Some uninstalls (notably macOS "Move to Bin") don't fire `beforeinstallprompt`
   * or update `getInstalledRelatedApps()` promptly, so nothing auto-clears the
   * persisted flag and the UI is stuck on a dead "Launch app". This lets the
   * screen return to the Install flow. If the app is in fact still installed, a
   * later `getInstalledRelatedApps()`/`appinstalled` re-marks it, so a misclick
   * self-heals on the next load.
   */
  markUninstalled(): void {
    this.installed.set(false);
    this.clearPersistedInstalled();
  }

  /**
   * Chromium-only positive confirmation: a non-empty result means an installed
   * instance exists, so we mark installed. An EMPTY result is deliberately
   * ignored — it is ambiguous (genuinely uninstalled, OR a `related_applications`
   * id/scope that failed to match), and treating it as "not installed" would
   * wrongly hide "Launch app" from a user who is actually installed. The
   * authoritative "not installed" signal is `beforeinstallprompt` (see above).
   */
  private detectExistingInstall(getInstalledRelatedApps: () => Promise<RelatedApplication[]>): void {
    void getInstalledRelatedApps()
      .then((apps) => {
        if (Array.isArray(apps) && apps.length > 0) this.markInstalled();
      })
      .catch(() => {
        // Best-effort — a failure just leaves the flag/beforeinstallprompt signals in charge.
      });
  }

  /**
   * Triggers the captured install prompt and reports the user's choice.
   * Returns `'unavailable'` when the browser never offered one (iOS Safari,
   * Firefox, or before `beforeinstallprompt` fired).
   */
  async promptInstall(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
    // Hold the event locally: `appinstalled` can fire (and null `this.deferredPrompt`) while
    // `prompt()` is still pending, which used to crash on `.userChoice` of null.
    const deferred = this.deferredPrompt;
    if (!deferred) return 'unavailable';
    // A deferred prompt is single-use; drop it up front so a double-click can't reuse it.
    this.deferredPrompt = null;
    this.canInstall.set(false);
    try {
      await deferred.prompt();
      const { outcome } = await deferred.userChoice;
      return outcome;
    } catch {
      // prompt() rejects if the event was already used — nothing left to offer.
      return 'unavailable';
    }
  }

  /** Sends the current tab to this env's deep link, focusing the installed app if present. */
  openApp(): void {
    window.location.href = pwaLaunchUrl();
  }
}
